import React from "react";
import { Alert, Avatar, Badge, Button, PageFilterBar, ButtonGroup, Card, Checkbox, Chip, ConfirmDialog, DataTable, DatePicker, Field, FiltersBar, Icon, ListingCard, ListingCardGrid, Menu, Modal, IconButton, Input, Money, NumberInput, Popover, Radio, RadioGroup, ScopeSelect, SearchInput, Segmented, Select, SortSelect, Skeleton, Spinner, SplitButton, StatePanel, StatusDot, Tabs, Tag, Textarea, Toggle, Tooltip, useToast } from "../components/ui";
import { HERO_MODIFIER } from "./connectionMap";

/**
 * componentRegistry — the single list of components documented in the styleguide,
 * each in the ComponentEntry shape. To document a NEW component: copy one object
 * below, swap the fields, and it renders identically. This is the "refer to the
 * existing one to build the next one" contract, made literal.
 *
 * Each entry:
 *   name, tier?, status, importFrom, sites[], blurb, demo (node), snippet, props[], guidance?
 */


function SegmentedDemo() {
  const [v, setV] = React.useState("active");
  return (
    <Segmented
      label="Listing status"
      value={v}
      onChange={setV}
      options={[
        { value: "all", label: "All", count: 128 },
        { value: "active", label: "Active", count: 96 },
        { value: "reserved", label: "Reserved", count: 7 },
        { value: "sold", label: "Sold", count: 25 },
      ]}
    />
  );
}

function RadioDemo() {
  const [v, setV] = React.useState("purchase");
  return (
    <RadioGroup legend="Deal type" value={v} onChange={setV} layout="cards">
      <Radio value="purchase" label="Straight purchase" description="Store buys the watch outright." />
      <Radio value="consignment" label="Consignment" description="Owner keeps title until sold." />
    </RadioGroup>
  );
}

function InputDemo() {
  const [q, setQ] = React.useState("Rolex Datejust");
  return (
    <>
      <Input placeholder="Reference number" aria-label="Reference" />
      <Input prefix="HK$" placeholder="0" inputMode="decimal" aria-label="Price" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} clearable round prefix={<Icon name="Search" size="sm" />} aria-label="Search" />
      <Input size="md" suffix="mm" defaultValue="41" aria-label="Case size" />
      <Input invalid defaultValue="ABC" aria-label="Invalid" />
      <Input disabled placeholder="Disabled" aria-label="Disabled" />
    </>
  );
}

const BRANDS = [
  { value: "rolex", label: "Rolex", group: "Swiss" },
  { value: "patek", label: "Patek Philippe", hint: "Geneva", group: "Swiss" },
  { value: "ap", label: "Audemars Piguet", group: "Swiss" },
  { value: "omega", label: "Omega", group: "Swiss" },
  { value: "cartier", label: "Cartier", group: "French" },
  { value: "seiko", label: "Grand Seiko", group: "Japanese" },
];
function SelectDemo() {
  const [one, setOne] = React.useState("rolex");
  const [many, setMany] = React.useState(["patek", "ap"]);
  const [created, setCreated] = React.useState(BRANDS);
  const [made, setMade] = React.useState(null);
  return (
    <>
      <Select label="Brand" options={BRANDS} value={one} onChange={setOne} clearable />
      <Select label="Brands" options={BRANDS} value={many} onChange={setMany} multi searchable />
      <Select
        label="Store"
        options={created}
        value={made}
        onChange={setMade}
        searchable
        placeholder="Pick or create a store"
        onCreate={(name) => { const o = { value: name.toLowerCase(), label: name }; setCreated((c) => [...c, o]); return o; }}
      />
      <Select label="Disabled" options={BRANDS} value="omega" onChange={() => {}} disabled />
    </>
  );
}

function SearchDemo() {
  const [q, setQ] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const onSearch = React.useCallback((text) => { if (!text) return; setBusy(true); setTimeout(() => setBusy(false), 700); }, []);
  return (
    <>
      <SearchInput value={q} onChange={(e) => setQ(e.target.value)} onClear={() => setQ("")} onSearch={onSearch} loading={busy} placeholder="Quick search for products" aria-label="Search products" />
      <SearchInput auto commandPalette placeholder="Search anything" aria-label="Global search" />
    </>
  );
}

function NumberDemo() {
  const [price, setPrice] = React.useState(9000);
  const [qty, setQty] = React.useState(2);
  const [pct, setPct] = React.useState(12.5);
  return (
    <>
      <NumberInput label="Price" money value={price} onChange={setPrice} />
      <NumberInput label="Quantity" value={qty} onChange={setQty} min={1} max={20} stepper size="md" />
      <NumberInput label="Commission" value={pct} onChange={setPct} decimals={1} suffix="%" size="md" />
    </>
  );
}

function DateDemo() {
  const [d, setD] = React.useState("2026-08-06");
  const [r, setR] = React.useState({ start: "2026-08-01", end: "2026-08-14" });
  const [ph, setPh] = React.useState("2026-08-06");
  return (
    <>
      <DatePicker label="Purchase date" value={d} onChange={setD} mode="calendar" />
      <DatePicker label="Report period" range value={r} onChange={setR} mode="calendar" />
      <DatePicker label="Phone rollers" value={ph} onChange={setPh} mode="rollers" size="md" />
    </>
  );
}

function ChipDemo() {
  const [f, setF] = React.useState({ stock: true, consignment: false, rolex: true });
  const toggle = (k) => setF((s) => ({ ...s, [k]: !s[k] }));
  return (
    <>
      <Chip selected={f.stock} onClick={() => toggle("stock")}>In stock</Chip>
      <Chip selected={f.consignment} onClick={() => toggle("consignment")} count={7}>Consignment</Chip>
      <Chip selected={f.rolex} onClick={() => toggle("rolex")} onRemove={() => setF((s) => ({ ...s, rolex: false }))} icon={<Icon name="Filter" size="xs" />}>Rolex</Chip>
      <Chip>Static chip</Chip>
    </>
  );
}

function TabsDemo() {
  const [v, setV] = React.useState("open");
  return (
    <>
      <Tabs
        aria-label="Listings sections"
        activePath="/listings"
        items={[
          { label: "Listings", to: "/listings" },
          { label: "Lookbooks", to: "/listings/lookbooks", count: 3 },
          { label: "Sold", to: "/listings/sold" },
          { label: "PriceIQ", to: "/priceiq", locked: true, lockedReason: "Included in the Pro plan" },
        ]}
      />
      <Tabs
        aria-label="Request state"
        variant="underline"
        value={v}
        onChange={setV}
        items={[
          { label: "Open", value: "open", count: 4 },
          { label: "Won", value: "won" },
          { label: "Lost", value: "lost" },
          { label: "Archived", value: "archived" },
        ]}
      />
    </>
  );
}

function FieldDemo() {
  const [ref, setRef] = React.useState("116500LN");
  const [notes, setNotes] = React.useState("");
  const [brand, setBrand] = React.useState("rolex");
  const [fail, setFail] = React.useState(false);
  const save = React.useCallback((v) => new Promise((ok, no) => setTimeout(() => (fail ? no(new Error("Network down")) : ok(v)), 700)), [fail]);
  return (
    <>
      <Field label="Reference" hint="As engraved between the lugs" required onSave={save}>
        <Input value={ref} onChange={(e) => setRef(e.target.value)} />
      </Field>
      <Field label="Brand" layout="inline" onSave={save} getValue={(v) => v}>
        <Select options={BRANDS} value={brand} onChange={setBrand} />
      </Field>
      <Field label="Internal notes" hint="Not shown to buyers" onSave={save}>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} minRows={2} />
      </Field>
      <Field label="Serial" error="A serial has 8 characters">
        <Input defaultValue="R12" />
      </Field>
      <Checkbox label="Simulate a failing network for the saves above" checked={fail} onChange={setFail} />
    </>
  );
}

function ModalDemo() {
  const [open, setOpen] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  return (
    <>
      <Button variant="secondary" onClick={() => setOpen("dialog")}>Confirm dialog</Button>
      <Button variant="secondary" onClick={() => setOpen("delete")}>Destructive confirm</Button>
      <Button variant="secondary" onClick={() => setOpen("drawer")}>Drawer (edit)</Button>
      <Button variant="secondary" onClick={() => setOpen("sheet")}>Bottom sheet</Button>
      <ConfirmDialog open={open === "dialog"} onClose={() => setOpen(null)} onConfirm={() => setOpen(null)} title="Publish this listing?" description="It will go live on all connected marketplaces." confirmLabel="Publish" />
      <ConfirmDialog open={open === "delete"} onClose={() => setOpen(null)} onConfirm={() => { setBusy(true); setTimeout(() => { setBusy(false); setOpen(null); }, 900); }} loading={busy} danger title="Delete this listing?" description="This permanently removes it and cannot be undone." confirmLabel="Delete" />
      <Modal open={open === "drawer"} onClose={() => setOpen(null)} variant="drawer" title="Edit price" description="Changes save as you type." footer={<Button onClick={() => setOpen(null)}>Done</Button>}>
        <div className="sg-stack"><Field label="Price"><Input prefix="HK$" defaultValue="9,000" /></Field><Field label="Notes"><Textarea defaultValue="Serviced June 2026" /></Field></div>
      </Modal>
      <Modal open={open === "sheet"} onClose={() => setOpen(null)} variant="sheet" title="Filters" footer={<Button fullWidth onClick={() => setOpen(null)}>Show 96 listings</Button>}>
        <div className="sg-stack"><Checkbox label="In stock" defaultChecked /><Checkbox label="Consignment" /><Checkbox label="Reserved" /></div>
      </Modal>
    </>
  );
}
function ToastDemo() {
  const toast = useToast();
  return (
    <>
      <Button variant="secondary" size="md" onClick={() => toast.success("Listing published", { description: "Live on Chrono24, eBay and WatchFinder.", action: { label: "Undo", onClick: () => {} } })}>Success + Undo</Button>
      <Button variant="secondary" size="md" onClick={() => toast.error("Could not save the price", { description: "Network down; we will retry." })}>Error</Button>
      <Button variant="secondary" size="md" onClick={() => toast.info("3 new buy alerts")}>Info</Button>
      <Button variant="secondary" size="md" onClick={() => toast.warning("PriceIQ has no data for this reference yet")}>Warning</Button>
    </>
  );
}

// Listing-card sample data. Local imagery only — the visual suite must never wait on a remote host.
const LISTING_PHOTO = "/assets/watch-gray.png";
const LISTING_LEDGER = {
  stockStatus: "In stock",
  cabinet: "Cabinet A-3",
  serial: "8K729104",
  purchase: 6200,
  costs: [
    { label: "Shipping", amount: 180 },
    { label: "Servicing", amount: 450 },
  ],
  marketValue: 10500,
  sale: {
    kind: "Sale request",
    number: "SR-1187",
    client: "Marcus Chen",
    collected: 3000,
    collectedOn: "12 Aug",
    due: 6000,
    dueState: "Pending",
  },
  reservedBy: "Sai Lau",
};

// Live specimen: the toggles auto-save (here, into local state) and the footer flip turns the top
// over. Two cards in the grid so the "every cell identical" rule is visible, not just asserted.
function ListingCardDemo() {
  const [first, setFirst] = React.useState({ active: true, reserved: false });
  const [second, setSecond] = React.useState({ active: false, reserved: true });
  return (
    <ListingCardGrid minColumn={360}>
      <ListingCard
        brand="Rolex"
        reference="115234-0002"
        model="Datejust 36 Oyster Perpetual Jubilee"
        price={9000}
        year={2025}
        condition="Excellent"
        image={LISTING_PHOTO}
        marketplaceCount="1/3"
        active={first.active}
        reserved={first.reserved}
        onActiveChange={(active) => setFirst((s) => ({ ...s, active }))}
        onReservedChange={(reserved) => setFirst((s) => ({ ...s, reserved }))}
        onEdit={() => {}}
        onOpen={() => {}}
        onMarketplaces={() => {}}
        ledger={LISTING_LEDGER}
      />
      <ListingCard
        brand="Omega"
        reference="310.30.42"
        model="Speedmaster Professional Moonwatch"
        price={null}
        year={2023}
        condition="Unworn"
        image={LISTING_PHOTO}
        marketplaceCount="0/3"
        active={second.active}
        reserved={second.reserved}
        onActiveChange={(active) => setSecond((s) => ({ ...s, active }))}
        onReservedChange={(reserved) => setSecond((s) => ({ ...s, reserved }))}
        onEdit={() => {}}
        onOpen={() => {}}
      />
    </ListingCardGrid>
  );
}

export const COMPONENT_REGISTRY = [
  {
    name: "Icon",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "The shared Lucide icon wrapper for every new interface control.",
    demo: (
      <>
        <Icon name="Search" size="xs" label="Extra-small search" />
        <Icon name="Search" size="sm" label="Small search" />
        <Icon name="Search" size="md" label="Medium search" />
        <Icon name="Search" size="lg" label="Large search" />
        <Icon name="Bell" size="xl" label="Notifications" />
      </>
    ),
    snippet: `<Icon name="Bell" size="md" label="Notifications" />`,
    props: [
      { prop: "name", values: "PascalCase name from ICONS", default: "—" },
      { prop: "size", values: "xs · sm · md · lg · xl", default: "md" },
      { prop: "label", values: "accessible icon name", default: "—" },
      { prop: "strokeWidth", values: "number", default: "1.75" },
      { prop: "className", values: "additional SVG classes", default: "—" },
    ],
    guidance: {
      do: ["Use Icon for every new icon and pass label when the icon conveys meaning."],
      dont: ["Use one set only — no FontAwesome in new code."],
    },
  },

  {
    name: "Button",
    tier: "cta-primary",
    status: "shared",
    importFrom: "components/ui",
    sites: [
      "listings/pages/RCListingsPage.jsx",
      "views/Orders/ordersTabs.js",
      "buyAlerts/RCBuyAlertsPage.jsx",
      "views/UsersPage.jsx",
      "stock/templates/CoreListTemplate.jsx",
    ],
    blurb: "The one text/CTA button — pill, indigo primary, four variants, four sizes, loading and icons built in. Matches the V2 button-* specimens.",
    demo: (
      <>
        <Button variant="primary" size="lg" hero>+ New Listing</Button>
        <Button variant="secondary">Secondary</Button>
        <Button variant="ghost">Ghost</Button>
        <Button variant="danger" size="sm">Delete</Button>
        <Button variant="primary" loading>Saving</Button>
        <Button variant="secondary" leadingIcon={<Icon name="Search" size="sm" />} hideLabelBelow="sm">Filters</Button>
        <Button variant="primary" disabled title="Pick a store first">Disabled</Button>
      </>
    ),
    snippet: `<Button variant="primary" size="lg" hero data-btn="listing-new">
  + New Listing
</Button>

<Button variant="secondary" leadingIcon={<Icon name="Filter" size="sm" />} hideLabelBelow="sm">
  Filters
</Button>`,
    props: [
      { prop: "variant", values: "primary · secondary · ghost · danger", default: "primary", note: "outline → secondary, icon → ghost, success/tab kept as legacy" },
      { prop: "size", values: "sm 28 · md 32 · lg 40 · xl 44", default: "md", note: "≥44px touch target on coarse pointers" },
      { prop: "loading", values: "boolean", default: "false", note: "spinner + aria-busy, blocks clicks" },
      { prop: "leadingIcon / trailingIcon", values: "node (usually <Icon />)", default: "—" },
      { prop: "hideLabelBelow", values: "sm · md", default: "—", note: "icon-only under 640 / 900px, label stays for AT" },
      { prop: "fullWidth", values: "boolean", default: "false", note: "phone action rows" },
      { prop: "hero", values: "boolean", default: "false", note: "page-header CTA: shared 156px min-width" },
      { prop: "align", values: "center · start", default: "center", note: "start: full-width disclosure row (section trigger) — label left, trailing icon right" },
      { prop: "to / href / nav", values: "route → Link · url → <a> · NavLink", default: "—" },
      { prop: "disabled + title", values: "string", default: "—", note: "title hoisted to a hoverable wrapper" },
      { prop: "ref", values: "forwarded", default: "—" },
    ],
    guidance: {
      do: [
        "One primary action per view; everything else secondary or ghost.",
        "Use loading for the async click instead of disabling by hand.",
        "Give every named button a data-btn=\"feature-action\" id.",
      ],
      dont: [
        "Don't hand-roll <button className=\"…\"> or reach for .rc-ui-btn — extend this component.",
        "Don't add a Save button — fields auto-save (see the guide's non-negotiable).",
        "Don't hardcode height, padding or colour — size and variant own them.",
      ],
    },
  },

  {
    name: "IconButton",
    tier: "icon-btn",
    status: "shared",
    importFrom: "components/ui",
    sites: ["components/layout/Topbar.jsx", "listings/pages/RCListingsPage.jsx", "views/Profile/VerificationCard.jsx"],
    blurb: "Icon-only control (edit, overflow, back, view switch). The accessible name is mandatory — pass label. Matches the V2 icon-default / icon-ghost specimens.",
    demo: (
      <>
        <IconButton label="Edit"><Icon name="Pencil" size="sm" /></IconButton>
        <IconButton label="More" variant="ghost"><Icon name="MoreHorizontal" size="sm" /></IconButton>
        <IconButton label="Back" size="lg"><Icon name="ChevronLeft" size="md" /></IconButton>
        <IconButton label="Grid view" pressed><Icon name="LayoutGrid" size="sm" /></IconButton>
        <IconButton label="Close" round variant="ghost"><Icon name="X" size="sm" /></IconButton>
        <IconButton label="Disabled" disabled><Icon name="Trash2" size="sm" /></IconButton>
      </>
    ),
    snippet: `<IconButton label="Back" size="lg" onClick={goBack}>
  <Icon name="ChevronLeft" size="md" />
</IconButton>

<IconButton label="Grid view" pressed={view === "grid"} onClick={() => setView("grid")}>
  <Icon name="LayoutGrid" size="sm" />
</IconButton>`,
    props: [
      { prop: "label", values: "string — REQUIRED", default: "—", note: "also accepted as aria-label; missing name logs an error in dev" },
      { prop: "variant", values: "default · ghost", default: "default", note: "back → default (legacy)" },
      { prop: "size", values: "sm 28 · md 32 · lg 40 · xl 44", default: "md", note: "hit area ≥ 44px on coarse pointers" },
      { prop: "pressed", values: "boolean", default: "—", note: "aria-pressed toggle state (grid/list, filter on)" },
      { prop: "round", values: "boolean", default: "false" },
      { prop: "loading / disabled", values: "boolean", default: "false" },
      { prop: "ref", values: "forwarded", default: "—" },
    ],
    guidance: {
      do: [
        "Pass an <Icon /> as the child; the button sizes itself, the icon keeps its own size (sm inside md, md inside lg).",
        "Use pressed for exclusive view switches instead of swapping variants.",
      ],
      dont: [
        "Don't ship one without a label — icon-only controls are invisible to screen readers.",
        "Don't put text inside: that's a Button with leadingIcon.",
      ],
    },
  },

  {
    name: "SplitButton",
    tier: "cta-primary",
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "One pill, two halves: the main action plus a caret that opens secondary actions. Composed from Button — whole pill hovers together (V2 split-button).",
    demo: (
      <>
        <SplitButton onClick={() => {}} items={[{ label: "Mark as sold", onSelect: () => {} }, { label: "Move to repair", onSelect: () => {} }, { label: "Delete listing", onSelect: () => {}, danger: true }]}>Change status</SplitButton>
        <SplitButton variant="secondary" onClick={() => {}} items={[{ label: "Export CSV", onSelect: () => {} }, { label: "Export PDF", onSelect: () => {} }]}>Export</SplitButton>
      </>
    ),
    snippet: `<SplitButton onClick={publish} items={[
  { label: "Mark as sold", onSelect: markSold },
  { label: "Delete listing", onSelect: remove, danger: true },
]}>
  Change status
</SplitButton>`,
    props: [
      { prop: "children / onClick", values: "main action label + handler", default: "—" },
      { prop: "items", values: "{ label, onSelect, icon?, danger?, disabled? }[]", default: "[]" },
      { prop: "variant", values: "primary · secondary", default: "primary" },
      { prop: "size", values: "sm · md · lg · xl", default: "md" },
      { prop: "menuLabel", values: "accessible name of the caret", default: "More options" },
      { prop: "fullWidth", values: "boolean", default: "false" },
    ],
    guidance: {
      do: ["Put the ONE most likely action in the main half; everything else in the menu."],
      dont: ["Don't use it for unrelated actions — that's a toolbar of Buttons.", "Don't nest a SplitButton in a ButtonGroup attached row."],
    },
  },

  {
    name: "ButtonGroup",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "Lays Buttons out as one row on laptop and stacks them full-width below 640px (phone action rows). Optional equal widths and attached (segmented) pill.",
    demo: (
      <>
        <ButtonGroup>
          <Button variant="secondary">Cancel</Button>
          <Button>Save</Button>
        </ButtonGroup>
        <ButtonGroup attached>
          <Button variant="secondary" size="sm">Day</Button>
          <Button variant="secondary" size="sm" active>Week</Button>
          <Button variant="secondary" size="sm">Month</Button>
        </ButtonGroup>
      </>
    ),
    snippet: `<ButtonGroup align="end" equal stackBelow="sm">
  <Button variant="secondary" onClick={close}>Cancel</Button>
  <Button onClick={confirm}>Confirm</Button>
</ButtonGroup>`,
    props: [
      { prop: "align", values: "start · center · end · stretch", default: "start" },
      { prop: "equal", values: "boolean — equal widths", default: "false" },
      { prop: "attached", values: "boolean — one segmented pill", default: "false" },
      { prop: "stackBelow", values: "sm (640) · md (900) · false", default: "sm" },
    ],
    guidance: {
      do: ["Use it for every pair of actions so phones get the stacked, full-width layout for free."],
      dont: ["Don't set widths on the buttons inside — equal/stretch own that."],
    },
  },

  {
    name: "Tabs",
    tier: "pill-tab",
    status: "shared",
    importFrom: "components/ui",
    sites: ["listings/templates/ListingsTemplate.jsx", "settings/SettingsTemplate.jsx", "views/Orders/OrdersHeader.jsx"],
    blurb: "V2 pill tabs (tab-default / hover / active) for page navigation, plus an underline variant for in-page sections. Routing mode (items with to) or controlled (value + onChange, tablist + arrow keys). Scrolls on phones keeping the active tab in view; locked tabs show a lock and a reason (entitlements).",
    demo: <TabsDemo />,
    snippet: `<Tabs items={[{ label: "Listings", to: "/listings" }, { label: "Lookbooks", to: "/listings/lookbooks" }]} />
<Tabs variant="underline" value={tab} onChange={setTab} items={[{ label: "Open", value: "open", count: 4 }]} />`,
    props: [
      { prop: "items", values: "{ label, to? | value?, exact?, icon?, count?, locked?, lockedReason?, disabled? }[]", default: "[]" },
      { prop: "value / onChange", values: "controlled mode (tablist)", default: "—" },
      { prop: "activePath", values: "routing mode: force the active path", default: "location" },
      { prop: "variant", values: "pill · underline", default: "pill" },
      { prop: "size", values: "sm 32 · md 40", default: "md" },
      { prop: "fullWidth / onLocked", values: "boolean · (item) => void", default: "false · —" },
    ],
    guidance: {
      do: ["Pill tabs for page-level navigation under the header; underline tabs inside a card or panel.", "Lock instead of hiding: people should see what the plan unlocks."],
      dont: ["Don't use Tabs as a filter — that's Segmented.", "Don't hand-roll .rc-ui-subtopbar__tab links."],
    },
  },

  {
    name: "Tooltip",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "Short explanation on hover / focus (300ms) or long-press on touch (V2 tooltip). The trigger is described by it (aria-describedby); Escape hides. Wrap ONE focusable element. Replaces react-tooltip.",
    demo: (
      <>
        <Tooltip content="WatchFinder rejected this listing — fix the price"><Badge tone="danger">WF</Badge></Tooltip>
        <Tooltip content="Publish to all connected marketplaces" side="bottom"><Button variant="secondary">Publish</Button></Tooltip>
        <Tooltip content="Edit"><IconButton label="Edit"><Icon name="Pencil" size="sm" /></IconButton></Tooltip>
      </>
    ),
    snippet: `<Tooltip content="Publish to all connected marketplaces">
  <IconButton label="Publish"><Icon name="Upload" size="sm" /></IconButton>
</Tooltip>`,
    props: [
      { prop: "content", values: "node — one sentence max", default: "—" },
      { prop: "children", values: "ONE focusable element", default: "—" },
      { prop: "side", values: "top · bottom · left · right (flips when cramped)", default: "top" },
      { prop: "delay", values: "ms", default: "300" },
      { prop: "disabled", values: "renders the child untouched", default: "false" },
    ],
    guidance: {
      do: ["Use it to explain an icon-only control or a disabled state — never to hide required information."],
      dont: ["Don't put links or buttons inside — that's a Popover.", "Don't import react-tooltip in new code."],
    },
  },

  {
    name: "Popover",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "Small anchored panel for menus, filter forms, help and pickers — the engine under Select, Menu and DatePicker. Closes on outside click / Escape / Tab-out, optional focus trap, returns focus to the trigger.",
    demo: (
      <>
        <Popover label="Quick filters" trigger={<Button variant="secondary" leadingIcon={<Icon name="SlidersHorizontal" size="sm" />}>Filters</Button>} trapFocus>
          {({ close }) => (
            <div className="sg-stack">
              <Checkbox label="In stock" defaultChecked />
              <Checkbox label="Consignment" />
              <Checkbox label="Reserved" />
              <Button size="md" onClick={close}>Apply</Button>
            </div>
          )}
        </Popover>
        <Popover label="Help" side="bottom" align="end" trigger={<IconButton label="Help"><Icon name="HelpCircle" size="sm" /></IconButton>}>
          PriceIQ compares your price with live listings on Chrono24, eBay and WatchFinder.
        </Popover>
      </>
    ),
    snippet: `<Popover label="Quick filters" trigger={<Button variant="secondary">Filters</Button>} trapFocus>
  {({ close }) => <FilterForm onApply={close} />}
</Popover>`,
    props: [
      { prop: "trigger", values: "ONE element — gets aria-haspopup / expanded / controls", default: "—" },
      { prop: "children", values: "node · ({ close }) => node", default: "—" },
      { prop: "open / onOpenChange", values: "controlled", default: "uncontrolled" },
      { prop: "side / align", values: "bottom · top · left · right / start · center · end", default: "bottom · start" },
      { prop: "trapFocus / matchWidth", values: "boolean", default: "false" },
      { prop: "label", values: "accessible name of the panel", default: "—" },
    ],
    guidance: {
      do: ["trapFocus when the panel holds a form; leave it off for a one-line menu."],
      dont: ["Don't nest a Popover in a Tooltip or vice versa."],
    },
  },

  {
    name: "Avatar",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "Identity mark (V2 avatar): image with initials fallback, sizes on the control scale, presence dot. Store variant is a rounded square with the country flag — the Master store gets a globe.",
    demo: (
      <>
        <Avatar name="Guillaume Barb" />
        <Avatar name="Sai Lau" size="sm" presence="online" />
        <Avatar name="Marcus Chen" size="lg" src="https://i.pravatar.cc/96?img=12" />
        <Avatar name="Hong Kong store" variant="store" flag="🇭🇰" />
        <Avatar name="London store" variant="store" flag="🇬🇧" size="sm" />
        <Avatar variant="store" master />
      </>
    ),
    snippet: `<Avatar name={user.name} src={user.avatar_url} size="sm" />
<Avatar variant="store" flag={store.flag} name={store.name} />`,
    props: [
      { prop: "name / src", values: "string · url", default: "—", note: "initials fallback on error" },
      { prop: "size", values: "xs 24 · sm 28 · md 40 · lg 48 · xl 64", default: "md" },
      { prop: "variant", values: "user · store", default: "user" },
      { prop: "flag / master", values: "emoji flag · boolean (globe)", default: "—" },
      { prop: "presence", values: "online · offline", default: "—" },
    ],
    guidance: { do: ["Store switcher: flag for a store, globe for the Master store — never the letters RC."], dont: ["Don't use an Avatar as a button — wrap it in IconButton."] },
  },

  {
    name: "Skeleton",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "Loading placeholders that keep the final layout (V2 skeleton-card): text lines, block, circle, and the card / row composites. Shimmer honours reduced motion.",
    demo: (
      <>
        <Skeleton lines={3} />
        <Skeleton shape="row" />
        <Skeleton shape="card" />
      </>
    ),
    snippet: `{isLoading ? <Skeleton shape="card" count={6} /> : listings.map(…)}`,
    props: [
      { prop: "shape", values: "text · block · circle · card · row", default: "text" },
      { prop: "lines", values: "number (text)", default: "3" },
      { prop: "width / height", values: "CSS length (block / circle)", default: "—" },
      { prop: "count", values: "repeat", default: "1" },
    ],
    guidance: { do: ["Match the shape to the content that will replace it — a card skeleton for a card grid."], dont: ["Don't show a Skeleton for under ~300ms — flash of placeholder is worse than nothing."] },
  },

  {
    name: "Spinner",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "The one loading indicator (V2 spinner): surface ring with an accent head. Inline in buttons and text, or standalone.",
    demo: (
      <>
        <Spinner size="xs" />
        <Spinner size="sm" />
        <Spinner />
        <Spinner size="lg" />
        <Spinner tone="neutral" />
        <span className="sg-inline"><Spinner size="sm" inline label="Saving" /> Saving…</span>
      </>
    ),
    snippet: `<Spinner size="sm" inline label="Saving" /> Saving…`,
    props: [
      { prop: "size", values: "xs 12 · sm 16 · md 24 · lg 32", default: "md" },
      { prop: "tone", values: "default (accent) · neutral · inverse", default: "default" },
      { prop: "inline", values: "boolean", default: "false" },
      { prop: "label", values: "announced text", default: "Loading" },
    ],
    guidance: { do: ["Prefer Button loading for a click, Skeleton for a page — Spinner for everything in between."], dont: ["Don't stack a Spinner on a Skeleton."] },
  },

  {
    name: "Field",
    covers: ["useAutosave"],
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "The auto-save composite: label · control · hint / error · Saving… → Saved ✓. Wrap ANY control; pass onSave and every change persists after a 600ms debounce (blur flushes, one automatic retry, then an error with Retry). No Save buttons — the field change is the commit.",
    demo: <FieldDemo />,
    snippet: `<Field label="Reference" hint="As engraved" required onSave={(v) => api.patch(id, { reference: v })}>
  <Input value={ref} onChange={(e) => setRef(e.target.value)} />
</Field>

const { status, commit } = useAutosave((v) => api.patch(id, { notes: v }));`,
    props: [
      { prop: "label / hint / error / required", values: "node · node · string · boolean", default: "—" },
      { prop: "children", values: "ONE control — gets id, aria-describedby, aria-invalid, invalid", default: "—" },
      { prop: "onSave", values: "(value) => Promise", default: "—", note: "enables auto-save" },
      { prop: "getValue", values: "(onChange arg) => value", default: "event.target.value or the value" },
      { prop: "debounce", values: "ms", default: "600" },
      { prop: "layout", values: "stacked · inline (label left ≥ 900px)", default: "stacked" },
      { prop: "status / statusError / onRetry", values: "external status when the parent owns the save", default: "—" },
    ],
    guidance: {
      do: ["Every editable value in the app lives in a Field with onSave — that is how the no-Save-button rule is kept.", "Validation errors go in error; save failures come from onSave automatically."],
      dont: ["Don't add a Save / Apply button under a Field.", "Don't debounce again in the parent — Field already does."],
    },
  },

  {
    name: "Modal",
    covers: ["ConfirmDialog"],
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "The one modal API (V2 modal / modal-delete): centred dialog for irreversible confirmations, right drawer for quick edits on laptop, bottom sheet on phones (drag handle, swipe down). Focus trap, Escape, scroll lock, stacked ladder, focus returned. Replaces react-modal and react-sliding-pane.",
    demo: <ModalDemo />,
    snippet: `<ConfirmDialog open={ask} onClose={() => setAsk(false)} onConfirm={remove} danger
  title="Delete this listing?" description="This permanently removes it." confirmLabel="Delete" />

<Modal open={editing} onClose={close} variant="drawer" title="Edit price" footer={<Button onClick={close}>Done</Button>}>…</Modal>`,
    props: [
      { prop: "open / onClose", values: "boolean · fn", default: "—" },
      { prop: "variant", values: "auto · dialog · drawer · sheet", default: "auto (sheet on phone, dialog otherwise)" },
      { prop: "title / description", values: "node — accessible name / description", default: "—" },
      { prop: "size", values: "sm · md · lg", default: "md" },
      { prop: "footer", values: "actions node", default: "—" },
      { prop: "dismissible / danger", values: "boolean", default: "true · false" },
      { prop: "ConfirmDialog", values: "onConfirm · confirmLabel · cancelLabel · danger · loading", default: "—" },
    ],
    guidance: {
      do: ["Dialog ONLY for irreversible actions (Delete, Publish, Send); everything else is a drawer or a sheet with auto-save inside.", "Red confirm button for destructive actions."],
      dont: ["No form with a Save button in a modal — fields auto-save; the footer is Done.", "No react-modal or react-sliding-pane in new code."],
    },
  },

  {
    name: "Menu",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "Action menus and three-dots everywhere: items with icon, shortcut, hint, danger tone, dividers, submenus. Laptop popover with roving tabindex and type-ahead; phone bottom-sheet action list. Replaces react-dropdown and ad-hoc menus.",
    demo: (
      <>
        <Menu label="Listing actions" trigger={<IconButton label="Listing actions"><Icon name="MoreHorizontal" size="sm" /></IconButton>} items={[
          { label: "Edit", icon: <Icon name="Pencil" size="sm" />, shortcut: "E", onSelect: () => {} },
          { label: "Mark as sold", icon: <Icon name="Check" size="sm" />, onSelect: () => {} },
          { label: "Move to", icon: <Icon name="ArrowRight" size="sm" />, items: [{ label: "In repair", onSelect: () => {} }, { label: "Reserved", onSelect: () => {} }] },
          { divider: true },
          { label: "Delete listing", icon: <Icon name="Trash2" size="sm" />, danger: true, onSelect: () => {} },
        ]} />
        <Menu label="Export" align="start" trigger={<Button variant="secondary" trailingIcon={<Icon name="ChevronDown" size="sm" />}>Export</Button>} items={[
          { label: "CSV", hint: "All columns", onSelect: () => {} },
          { label: "PDF stock list", onSelect: () => {} },
          { label: "Lookbook", disabled: true },
        ]} />
      </>
    ),
    snippet: `<Menu label="Listing actions" trigger={<IconButton label="Actions"><Icon name="MoreHorizontal" size="sm" /></IconButton>}
  items={[{ label: "Edit", onSelect: edit }, { divider: true }, { label: "Delete", danger: true, onSelect: remove }]} />`,
    props: [
      { prop: "trigger", values: "ONE element", default: "—" },
      { prop: "items", values: "{ label, onSelect, icon?, shortcut?, hint?, danger?, disabled?, items? } | { divider: true }", default: "[]" },
      { prop: "label", values: "accessible name", default: "Actions" },
      { prop: "side / align", values: "positioning", default: "bottom · end" },
    ],
    guidance: { do: ["Same items array for the ⋯ on a row, a card and the split-button caret."], dont: ["No more than one level of submenu."] },
  },

  {
    name: "Alert",
    covers: ["ToastProvider", "useToast", "useAlert"],
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "Feedback system replacing react-alert. useToast() → queued toasts (bottom-left on laptop, above the tab bar on phones) with tones, description, action (Undo), auto-dismiss; Alert = inline banner for page-level states (V2 alert-*). useAlert() is a drop-in shim for the 38 legacy alert.show() call sites.",
    demo: (
      <>
        <ToastDemo />
        <Alert tone="info" title="Heads up">This listing has no competitive price yet.</Alert>
        <Alert tone="success">Saved — live on all marketplaces.</Alert>
        <Alert tone="warning" action={{ label: "Review", onClick: () => {} }}>3 listings are priced below PriceIQ.</Alert>
        <Alert tone="error" title="Could not save." onDismiss={() => {}}>Check the highlighted fields.</Alert>
      </>
    ),
    snippet: `const toast = useToast();
toast.success("Listing published", { action: { label: "Undo", onClick: undo } });

<Alert tone="warning" action={{ label: "Review", onClick: open }}>3 listings are priced below PriceIQ.</Alert>`,
    props: [
      { prop: "toast(opts)", values: "{ title, description?, tone?, action?, duration? } · .success / .error / .info / .warning", default: "—" },
      { prop: "Alert tone", values: "info · success · warning · error", default: "info" },
      { prop: "Alert title / action / onDismiss", values: "node · { label, onClick } · fn", default: "—" },
    ],
    guidance: { do: ["Toast for the result of an action; Alert for a state the page is in."], dont: ["No toasts for validation errors — they belong next to the field."] },
  },

  {
    name: "Toggle",
    covers: ["ToggleButton"],
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [
      "listings/components/RCListingCard.jsx",
      "buyAlerts/RCBuyAlertsPage.jsx",
      "views/Leads.jsx",
      "stock/templates/CoreListTable.jsx",
    ],
    blurb: "Auto-saving switch (Active, Reserved, In stock…). Return a promise from onChange and it updates optimistically, rolls back and shows the error inline. Matches the V2 toggle-* specimens.",
    demo: <ToggleDemo />,
    snippet: `<Toggle label="Active" tone="active"
        checked={active} onChange={setActive} />`,
    props: [
      { prop: "checked", values: "boolean (controlled)", default: "false" },
      { prop: "onChange", values: "(next) => void | Promise", default: "—", note: "promise → optimistic + rollback + inline error" },
      { prop: "tone", values: "default · active · reserved", default: "default", note: "accent · success · warning when on" },
      { prop: "size", values: "sm 36×20 · md 44×26", default: "md" },
      { prop: "label / labelPosition", values: "node · start | end", default: "— · end" },
      { prop: "statusOn / statusOff", values: "short text beside the switch", default: "—", note: "listing card: ON / OFF · RSVD" },
      { prop: "bare", values: "legacy, ignored", default: "—" },
    ],
    guidance: {
      do: ["Keep it controlled — own the state and handle onChange.", "Persist on change — never behind a Save button."],
      dont: ["Don't use the listings/atoms/ToggleButton fork — migrate to this."],
    },
  },

  {
    name: "Checkbox",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "Native checkbox with the V2 box (20px, accent when on). Indeterminate for bulk selection; label, description and inline error built in.",
    demo: (
      <>
        <Checkbox label="Publish to WatchFinder" defaultChecked />
        <Checkbox label="Select all" indeterminate description="3 of 12 selected" />
        <Checkbox label="Agree to the consignment terms" error="Required to continue" />
        <Checkbox label="Disabled" disabled />
      </>
    ),
    snippet: `<Checkbox label="Select all" checked={all} indeterminate={some && !all}
  onChange={(next) => toggleAll(next)} />`,
    props: [
      { prop: "checked / defaultChecked", values: "boolean", default: "—" },
      { prop: "indeterminate", values: "boolean → aria-checked=mixed", default: "false" },
      { prop: "onChange", values: "(next: boolean, event) => void", default: "—" },
      { prop: "label / description / error", values: "node · node · string", default: "—" },
      { prop: "ref", values: "forwarded to the <input>", default: "—" },
    ],
    guidance: {
      do: ["Use indeterminate on the header checkbox of a selectable table."],
      dont: ["Don't use a Checkbox for an instant setting — that's a Toggle (auto-saved)."],
    },
  },

  {
    name: "RadioGroup",
    covers: ["Radio"],
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "Exclusive choice from a short list (V2 radio-group). Native radios in a fieldset: arrow keys work out of the box. layout=cards for big decisions.",
    demo: <RadioDemo />,
    snippet: `<RadioGroup legend="Deal type" value={type} onChange={setType} layout="cards">
  <Radio value="purchase" label="Straight purchase" />
  <Radio value="consignment" label="Consignment" />
</RadioGroup>`,
    props: [
      { prop: "value / defaultValue / onChange", values: "controlled or uncontrolled", default: "—" },
      { prop: "legend / hideLegend", values: "node · boolean", default: "— · false" },
      { prop: "layout", values: "list · inline · cards", default: "list" },
      { prop: "error", values: "string → aria-invalid + alert", default: "—" },
      { prop: "Radio: value / label / description", values: "string · node · node", default: "—" },
    ],
    guidance: {
      do: ["2–5 options, all visible: radios. More than that: Select."],
      dont: ["Don't use radios for a yes/no — use a Toggle or a Checkbox."],
    },
  },

  {
    name: "Segmented",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "Exclusive inline choice for filters and view modes (All / Active / Reserved / Sold, grid / list). Arrow keys move the selection; scrolls with an edge fade on phones.",
    demo: <SegmentedDemo />,
    snippet: `<Segmented label="Listing status" value={status} onChange={setStatus}
  options={[{ value: "all", label: "All", count: 128 }, { value: "active", label: "Active" }]} />`,
    props: [
      { prop: "options", values: "{ value, label, icon?, count?, disabled? }[]", default: "[]" },
      { prop: "value / onChange", values: "controlled", default: "—" },
      { prop: "size", values: "sm · md", default: "md" },
      { prop: "label", values: "accessible name — REQUIRED", default: "—" },
      { prop: "fullWidth", values: "boolean", default: "false" },
    ],
    guidance: {
      do: ["Use it for the status filter row on every list page — one control, one look."],
      dont: ["Don't use it for navigation between pages — that's Tabs."],
    },
  },

  {
    name: "Input",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: ["settings/molecules/SharedCatalogAdminPanel.jsx", "settings/molecules/StoreCatalogPanel.jsx"],
    blurb: "Text control on the V2 input-* contract: 44px shell, prefix/suffix slots (currency, unit, icon), clear button, lilac focus ring, danger error ring. Label, hint and error live in Field.",
    demo: <InputDemo />,
    snippet: `<Input prefix="HK$" inputMode="decimal" value={price} onChange={e => setPrice(e.target.value)} />
<Input round clearable prefix={<Icon name="Search" size="sm" />} value={q} onChange={…} onClear={() => setQ("")} />`,
    props: [
      { prop: "size", values: "md 40 · lg 44", default: "lg", note: "sm/xl/2xl legacy → md/lg" },
      { prop: "prefix / suffix", values: "node", default: "—", note: "legacy leading / trailing" },
      { prop: "clearable / onClear", values: "boolean · fn", default: "false" },
      { prop: "invalid", values: "boolean → aria-invalid + ring", default: "false" },
      { prop: "round", values: "pill radius (search)", default: "false", note: "legacy pill" },
      { prop: "fullWidth", values: "boolean", default: "false" },
      { prop: "ref", values: "forwarded to <input>", default: "—" },
    ],
    guidance: {
      do: ["Pair with Field for label + hint + error + auto-save status.", "Use inputMode so phones show the right keyboard."],
      dont: ["Don't set a width on the input — the Field / grid owns layout.", "Don't put money in a plain Input — use Money / NumberInput."],
    },
  },

  {
    name: "Textarea",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "Multi-line text that grows with its content up to maxRows, with an optional character counter.",
    demo: (
      <>
        <Textarea placeholder="Internal notes…" minRows={2} maxRows={6} aria-label="Notes" />
        <Textarea defaultValue="Serviced by our watchmaker in June; new gaskets, pressure tested." maxLength={120} aria-label="Condition" />
      </>
    ),
    snippet: `<Textarea value={notes} onChange={e => setNotes(e.target.value)} minRows={2} maxRows={8} maxLength={500} />`,
    props: [
      { prop: "minRows / maxRows", values: "number", default: "2 · 8" },
      { prop: "maxLength", values: "number → counter, danger when over", default: "—" },
      { prop: "invalid", values: "boolean", default: "false" },
      { prop: "fullWidth", values: "boolean", default: "true" },
    ],
    guidance: {
      do: ["Let it grow — never a fixed height with an inner scrollbar under maxRows."],
      dont: ["Don't use it for one-line values that happen to be long — that's an Input."],
    },
  },

  {
    name: "Select",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "The one dropdown (replaces react-select and react-dropdown): single or multi, searchable, async options, create-inline, groups, hints. Laptop: popover matching the trigger width; phone: bottom sheet. V2 select-default / select-open.",
    demo: <SelectDemo />,
    snippet: `<Select label="Brand" options={brands} value={brand} onChange={setBrand} clearable />
<Select label="Stores" multi searchable options={stores} value={ids} onChange={setIds} />
<Select label="Watchmaker" loadOptions={q => api.searchWatchmakers(q)} value={id} onChange={setId}
  onCreate={name => api.createWatchmaker(name)} />`,
    props: [
      { prop: "options", values: "{ value, label, hint?, icon?, disabled?, group? }[]", default: "[]" },
      { prop: "value / onChange", values: "value · (value, option) — arrays when multi", default: "—" },
      { prop: "multi", values: "boolean → chips in the trigger", default: "false" },
      { prop: "searchable", values: "boolean", default: "auto (async or > 8 options)" },
      { prop: "loadOptions", values: "(query) => Promise<Option[]>", default: "—", note: "debounced 250ms" },
      { prop: "onCreate", values: "(label) => Option | Promise<Option>", default: "—", note: "adds a Create “x” row" },
      { prop: "clearable / invalid / disabled", values: "boolean", default: "false" },
      { prop: "searchPlaceholder", values: "string — what the search box searches", default: "Type to filter…" },
      { prop: "size", values: "md 40 · lg 44", default: "md" },
      { prop: "label", values: "accessible name (or aria-labelledby from Field)", default: "—" },
    ],
    guidance: {
      do: ["Reach for Select for any list longer than 5; radios below that.", "Pass hint for secondary text (city, count) instead of concatenating into the label."],
      dont: ["Don't import react-select or react-dropdown in new code — the lint fence blocks it.", "Don't render a Select inside a scrolling container without letting it portal (it always portals)."],
    },
  },

  {
    name: "NumberInput",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "Numbers and money typed by people (replaces react-currency-input-field): value is a number, text is grouped en-GB on blur, currency symbol prefix (V2 input-money), min/max clamp, step via arrows or the optional stepper.",
    demo: <NumberDemo />,
    snippet: `<NumberInput label="Price" money currency={listing.currency} value={price} onChange={setPrice} />
<NumberInput label="Quantity" value={qty} onChange={setQty} min={1} stepper />`,
    props: [
      { prop: "value / onChange", values: "number | null", default: "—", note: "major units — convert to pence at the API edge" },
      { prop: "money / currency", values: "boolean · ISO code", default: "false · base", note: "symbol prefix from core/currency" },
      { prop: "min / max / step", values: "number", default: "— · — · 1" },
      { prop: "decimals", values: "fraction digits on blur", default: "money: 0 or 2" },
      { prop: "stepper", values: "boolean → − / + buttons", default: "false" },
      { prop: "suffix", values: "unit text (mm, %)", default: "—" },
    ],
    guidance: {
      do: ["Keep values in major units in state; multiply by 100 only when sending (the ×100 rule lives in the helpers)."],
      dont: ["Don't use type=\"number\" inputs — no grouping, broken on iOS, spinner noise."],
    },
  },

  {
    name: "Money",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "The one way an amount is displayed: tabular numerals, currency-aware via core/currency, negatives with a true minus, POR, optional base-currency bracket (Q9), tones and sizes.",
    demo: (
      <>
        <Money value={900000} currency="hkd" />
        <Money value={125000} currency="usd" rateToBase={7.81} />
        <Money value={-4500} tone="danger" />
        <Money value={0} porOnZero />
        <Money value={1850} major size="lg" tone="success" signed />
        <Money value={null} />
      </>
    ),
    snippet: `<Money value={listing.price} currency={listing.currency} />
<Money value={order.total} currency={order.currency} rateToBase={rate} size="lg" />`,
    props: [
      { prop: "value", values: "minor units (pence) · null", default: "—" },
      { prop: "major", values: "value already in major units", default: "false" },
      { prop: "currency", values: "ISO code", default: "store base" },
      { prop: "rateToBase", values: "number → (base) bracket when currency ≠ base", default: "—" },
      { prop: "por / porOnZero", values: "Price on request", default: "false" },
      { prop: "tone", values: "default · muted · strong · success · danger", default: "default" },
      { prop: "size", values: "sm · md · lg · xl", default: "md" },
      { prop: "signed", values: "always show + / −", default: "false" },
    ],
    guidance: {
      do: ["Every money surface goes through Money — never string-concatenate a symbol."],
      dont: ["Don't round or convert in the view; pass the stored value and its currency."],
    },
  },

  {
    name: "DatePicker",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "Dates the way the business reads them: DD-MM-YYYY, always. Values are date-only ISO strings (no Date objects, no timezone drift). Phone: the V2 drag rollers in a sheet. Laptop: calendar popover. Range variant for reports. Replaces react-datepicker.",
    demo: <DateDemo />,
    snippet: `<DatePicker label="Purchase date" value={date} onChange={setDate} max={todayIso} />
<DatePicker label="Period" range value={{ start, end }} onChange={setPeriod} />`,
    props: [
      { prop: "value / onChange", values: "\"YYYY-MM-DD\" | null · { start, end } when range", default: "—" },
      { prop: "range", values: "boolean", default: "false" },
      { prop: "min / max", values: "ISO bounds — cells disabled, typed values clamped", default: "—" },
      { prop: "mode", values: "auto · rollers · calendar", default: "auto (rollers on phone)" },
      { prop: "clearable / invalid / disabled", values: "boolean", default: "true · false · false" },
      { prop: "size", values: "md 40 · lg 44", default: "lg" },
      { prop: "label", values: "accessible name — REQUIRED", default: "—" },
    ],
    guidance: {
      do: ["Keep dates as YYYY-MM-DD strings end to end (core/dateOnly) — format only at the edge.", "Let people type: 06-08-2026, 6/8/2026 and 6.8.2026 all parse."],
      dont: ["Don't render dates month-first anywhere — the business reads day-month-year.", "Don't import react-datepicker in new code."],
    },
  },

  {
    name: "SearchInput",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [
      "listings/pages/RCListingsPage.jsx",
      "views/UsersPage.jsx",
      "components/QuickFilter.jsx",
      "stock/templates/CoreListTable.jsx",
    ],
    blurb: "The canonical search bar (V2 search-default / search-filled): round Input with magnifier, clear ×, debounced onSearch, loading spinner, Esc clears, ⌘K opens the command palette. Elastic on a filter row, full width on phones.",
    demo: <SearchDemo />,
    snippet: `<SearchInput value={q} onChange={e => setQ(e.target.value)} onClear={() => setQ("")}
  onSearch={runSearch} loading={isFetching} placeholder="Quick search for products" />`,
    props: [
      { prop: "value / onChange", values: "controlled (or leave uncontrolled)", default: "—" },
      { prop: "onSearch", values: "(query) => void — debounced", default: "—", note: "debounce 300ms" },
      { prop: "onClear", values: "fn — × and Esc", default: "—" },
      { prop: "loading", values: "boolean → spinner", default: "false" },
      { prop: "auto", values: "natural width instead of absorbing the row", default: "false" },
      { prop: "commandPalette", values: "⌘K / Ctrl+K → ft:command-palette event + hint", default: "false" },
      { prop: "size", values: "md 40 · lg 44", default: "lg" },
    ],
    guidance: {
      do: ["Fire the request in onSearch, not onChange — the debounce is the point.", "One SearchInput per list page, in the filter row, before the Segmented status filter."],
      dont: ["Don't hand-roll a search <input> or restyle this one per page — add a prop here."],
    },
  },

  {
    name: "Badge",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: ["listings/components/RCListingCard.jsx", "stock/templates/CoreListTable.jsx"],
    blurb: "Compact status / count pill (V2 badge-*). Pass status and the tone + sentence-case label come from the one status map: indigo active · green sold/paid · amber reserved/pending · grey repair/inactive · red error.",
    demo: (
      <>
        <Badge status="active" />
        <Badge status="sold" />
        <Badge status="reserved" />
        <Badge status="in_repair" />
        <Badge status="error" />
        <Badge status="consignment" />
        <Badge tone="accent" dot>New</Badge>
        <Badge tone="danger" count>12</Badge>
        <Badge tone="muted" size="sm">Draft</Badge>
      </>
    ),
    snippet: `<Badge status={listing.status} />
<Badge tone="danger" count>{unread}</Badge>`,
    props: [
      { prop: "status", values: "business status string → tone + label", default: "—" },
      { prop: "tone", values: "accent · success · warning · danger · muted · info", default: "muted", note: "legacy default → muted, purple → accent" },
      { prop: "size", values: "sm · md", default: "md" },
      { prop: "dot / count", values: "boolean", default: "false" },
    ],
    guidance: {
      do: ["Always pass status for business states — never pick a colour by hand.", "Sentence case: Reserved, In repair — never RESERVED."],
      dont: ["Don't use a Badge as a button — that's a Chip."],
    },
  },

  {
    name: "Chip",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "Interactive filter pill (V2 chip-default / chip-active): click toggles (aria-pressed), × removes, optional icon and count.",
    demo: <ChipDemo />,
    snippet: `<Chip selected={f.on} onClick={() => toggle(f)} onRemove={() => remove(f)} count={f.count}>{f.label}</Chip>`,
    props: [
      { prop: "selected", values: "boolean → aria-pressed", default: "false" },
      { prop: "onClick", values: "fn — makes it a button", default: "—" },
      { prop: "onRemove", values: "fn — shows ×", default: "—" },
      { prop: "icon / count", values: "node · number", default: "—" },
    ],
    guidance: {
      do: ["Use Chips for applied filters above a list; Segmented for the exclusive status filter."],
      dont: ["Don't show a status in a Chip — that's a Badge."],
    },
  },

  {
    name: "Tag",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "Static label: brand, category, reference number (mono). Never clickable, never a status.",
    demo: (
      <>
        <Tag>Rolex</Tag>
        <Tag icon={<Icon name="Tag" size="xs" />}>Sports</Tag>
        <Tag mono>115234-0002</Tag>
        <Tag size="sm">2026</Tag>
      </>
    ),
    snippet: `<Tag mono>{listing.reference}</Tag>`,
    props: [
      { prop: "icon", values: "node", default: "—" },
      { prop: "size", values: "sm · md", default: "md" },
      { prop: "mono", values: "boolean — reference numbers", default: "false" },
    ],
    guidance: { do: ["Use for metadata that is not a state."], dont: ["Don't colour a Tag — colour means status."] },
  },

  {
    name: "StatusDot",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "8px status dot for list rows and kanban cards, with a text label (or aria-label) so colour is never the only signal. pulse for live / in-progress.",
    demo: (
      <>
        <StatusDot status="active" />
        <StatusDot status="sold" />
        <StatusDot status="pending" pulse />
        <StatusDot status="in_repair" />
        <StatusDot status="error" />
        <StatusDot tone="success" aria-label="Paid" />
      </>
    ),
    snippet: `<StatusDot status={order.status} />
<StatusDot status="processing" pulse>Syncing…</StatusDot>`,
    props: [
      { prop: "status / tone", values: "status string · explicit tone", default: "—" },
      { prop: "children", values: "visible label", default: "status in sentence case" },
      { prop: "pulse", values: "boolean", default: "false" },
    ],
    guidance: { do: ["Prefer StatusDot in dense rows, Badge in cards and headers."], dont: ["Don't ship a dot with neither children nor aria-label."] },
  },

  {
    name: "StatePanel",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: ["/stock/core-list"],
    blurb: "The one application-state surface (V2 empty-first-run / empty-no-results / empty-error / skeleton-card): the six things a page shows instead of its content — first run, no results, loading, a recoverable error, no permission, and not in the plan. Owns no routing: every action is caller-supplied, so the same panel serves any page.",
    demo: (
      <>
        <StatePanel
          state="empty"
          title="No listings yet"
          description="Add your first watch and it'll show up here, ready to price and publish."
          action={{ label: "Add a listing" }}
        />
        <StatePanel
          state="no-results"
          title="No matches for “Daytona”"
          action={{ label: "Clear filters", variant: "secondary" }}
        />
        <StatePanel state="loading" title="Loading listings…" loadingShape="card" loadingCount={1} />
        <StatePanel
          state="error"
          title="Couldn't load listings"
          description="The request timed out. Your work is safe — try again."
          onRetry={() => {}}
        />
        <StatePanel state="permission" />
        <StatePanel state="locked" action={{ label: "See plans", variant: "secondary" }} />
      </>
    ),
    snippet: `{isError ? (
  <StatePanel state="error" title="Couldn't load the Core List" description="Please try again." onRetry={refetch} />
) : isLoading ? (
  <StatePanel state="loading" title="Loading…" loadingShape="row" loadingCount={4} />
) : !rows.length ? (
  <StatePanel state="empty" title="Nothing here yet" action={{ label: "Add products", onClick: open }} />
) : (
  <Table rows={rows} />
)}`,
    props: [
      { prop: "state", values: "empty · no-results · loading · error · permission · locked", default: "empty" },
      { prop: "title / description", values: "override the state's copy (null hides it)", default: "per state" },
      { prop: "icon", values: "Icon name · node · false", default: "per state" },
      { prop: "action / secondaryAction", values: "{ label, onClick | to | href, variant, size } or a node", default: "—" },
      { prop: "onRetry", values: "shorthand Retry for the error state", default: "—" },
      { prop: "loadingShape / loadingCount", values: "text · card · row · block / repeat", default: "text / 1" },
      { prop: "headingLevel", values: "2 · 3 · 4", default: "3" },
      { prop: "compact", values: "tighter padding for in-table slots", default: "false" },
    ],
    guidance: {
      do: [
        "Match loadingShape to the content that replaces it — row for a table, card for a grid.",
        "Keep permission and entitlement copy apart: permission never names a plan, locked never blames the user.",
      ],
      dont: [
        "Don't hard-code a route inside the panel — pass the action from the page that owns it.",
        "Don't pair a StatePanel error with a toast for the same failure; one message per failure.",
      ],
    },
  },

  {
    name: "Card",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "The generic panel surface (V2 card-solid / card-raised): a hairline border, the card radius and a raised background. It knows nothing about its contents on purpose — anything with a fixed internal design is its own composite, not a Card variant.",
    demo: (
      <>
        <Card>
          <strong>Default surface</strong>
          <p>Sits directly on the page.</p>
        </Card>
        <Card surface="raised" padding="lg">
          <strong>Raised, large padding</strong>
          <p>For a panel stacked on another surface.</p>
        </Card>
        <Card as="button" interactive onClick={() => {}}>
          <strong>Interactive</strong>
          <p>Hover lifts it; Enter and Space activate it.</p>
        </Card>
      </>
    ),
    snippet: `<Card as="section" surface="raised" padding="lg" aria-label="Deal summary">
  <h3>Deal summary</h3>
  <Money value={183000} />
</Card>

// Clickable panel — real activation semantics, no synthesised role needed.
<Card as="button" interactive onClick={openListing}>…</Card>`,
    props: [
      { prop: "as", values: "div · section · article · li · button · a · component", default: "div" },
      { prop: "surface", values: "default · raised", default: "default" },
      { prop: "padding", values: "none · sm · md · lg", default: "md" },
      { prop: "interactive", values: "hover lift + focus ring; wires Enter/Space when onClick is set", default: "false" },
      { prop: "onClick", values: "click handler; with interactive it is keyboard-activatable", default: "—" },
      { prop: "className / children / ref", values: "passthrough", default: "—" },
    ],
    guidance: {
      do: [
        "Pick the element that describes the content — as=\"section\" for a labelled panel, as=\"li\" inside a list.",
        "Use padding=\"none\" when the content goes edge to edge (a photo, a table, its own rows).",
        "Prefer as=\"button\" or as=\"a\" for a whole-card action; the browser's semantics beat a synthesised role.",
      ],
      dont: [
        "Don't nest a button inside an interactive Card — one card, one action, or make the inner controls the only targets.",
        "Don't rebuild the listing card out of Card; ListingCard is pixel-frozen and owns its own geometry.",
      ],
    },
  },

  {
    name: "ListingCard",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb: "The pixel-frozen V2 watch card: toggles lifted onto the photo, a frozen 468×332 internal design, ON/OFF and RSVD auto-save toggles, and a top-only flip to the private deal ledger. ListingCardGrid scales the whole card uniformly into its column — it never reflows, so every card in a row is identical and the model title stays on one line.",
    demo: <ListingCardDemo />,
    snippet: `<ListingCardGrid minColumn={360}>
  {listings.map((l) => (
    <ListingCard
      key={l.id}
      brand={l.brand}
      reference={l.reference}
      model={l.model}
      price={l.price}              // major units; null renders "Price on request"
      year={l.year}
      condition={l.condition}
      image={l.photo}
      marketplaceCount={\`\${l.published}/\${l.channels}\`}
      active={l.active}
      reserved={l.reserved}
      onActiveChange={(next) => saveActive(l.id, next)}   // return the promise for rollback
      onReservedChange={(next) => saveReserved(l.id, next)}
      onEdit={() => openDrawer(l.id)}
      onOpen={() => navigate(\`/listing/\${l.id}\`)}
      onMarketplaces={() => openChannels(l.id)}
      ledger={l.ledger}            // omit it and the flip control disappears
    />
  ))}
</ListingCardGrid>`,
    props: [
      { prop: "brand / reference / model", values: "front lines 1 and 2; model is one line with an ellipsis", default: "—" },
      { prop: "price / currency", values: "major units, null → Price on request / ISO code", default: "— / store base" },
      { prop: "year / condition", values: "front line 3", default: "—" },
      { prop: "image / imageAlt", values: "photo src; alt defaults to brand + model + reference", default: "—" },
      { prop: "marketplaceCount", values: "the footer count, e.g. \"1/3\"", default: "—" },
      { prop: "active / reserved", values: "CONTROLLED switch states", default: "false" },
      { prop: "onActiveChange / onReservedChange", values: "(next) => void | Promise — auto-saves, promise gives rollback", default: "—" },
      { prop: "onEdit / onOpen / onMarketplaces", values: "front actions; omitted actions are not rendered", default: "—" },
      { prop: "flipped / onFlippedChange", values: "controlled flip / fires on every flip", default: "internal state" },
      { prop: "ledger", values: "{ stockStatus, cabinet, serial, purchase, costs[], marketValue, sale{}, reservedBy }", default: "—" },
      { prop: "disabled", values: "locks both switches", default: "false" },
      { prop: "ListingCardGrid minColumn", values: "narrowest column before a column is dropped", default: "360" },
    ],
    guidance: {
      do: [
        "Always render it inside ListingCardGrid or ListingCardCell — the cell is what scales the frozen 468×332 card to the column.",
        "Return the save promise from onActiveChange / onReservedChange so the switch can roll back on failure.",
        "Pass money as numbers in major units and let the card format them; the ledger arithmetic depends on it.",
      ],
      dont: [
        "Don't restyle the internals or add a phone layout — the design is frozen and scales uniformly instead.",
        "Don't add a Save button; the toggles persist on change, which is the whole listing-card contract.",
        "Don't repeat anything from the front on the ledger back — the back exists for what the front cannot show.",
      ],
    },
  },

  {
    name: "DataTable",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: [],
    blurb:
      "One tabular surface with two presentations of the same data, from one set of column definitions: a 36px compact-row table from 900px up, one Card per row below it. Exactly one presentation mounts, so a cell renderer runs once per row at either width — its buttons, links, toggles and disabled state cross the switch untouched. Sorting is controlled; the six application states are StatePanel's.",
    demo: <DataTableDemo />,
    snippet: `const columns = [
  { key: "reference", header: "Reference", card: "title",
    cell: (row) => <Link to={\`/view-product/\${row.product_id}\`}>{row.reference}</Link> },
  { key: "brand", header: "Brand", card: "subtitle" },
  { key: "status", header: "Stock state", card: "aside",
    cell: (row) => <Badge status={row.status} /> },
  { key: "demand", header: "Demand 30d", align: "end" },   // no role -> a labelled meta pair
  { key: "listing", header: "Listing",
    cell: (row) => <Toggle checked={row.active} onChange={save(row)} disabled={row.busy} /> },
  { key: "actions", header: "Actions", headerHidden: true, card: "actions",
    cell: (row) => <Button size="md" variant="outline" onClick={() => remove(row)}>Remove</Button> },
];

<DataTable
  caption="Core List references"          // required: names the table for a screen reader
  columns={columns}
  rows={rows}                              // already in the order to display
  rowKey="core_list_item_id"
  sort={sort}                              // controlled — DataTable sorts nothing itself
  onSortChange={(field, direction) => setSort({ field, direction })}
  rowTo={(row) => \`/view-product/\${row.product_id}\`}
  linkComponent={Link}                     // react-router stays out of the primitive
  state={isLoading ? "loading" : error ? "error" : rows.length ? null : "empty"}
  stateProps={{ onRetry: refetch }}
  footer={<Pager … />}
/>`,
    props: [
      { prop: "columns", values: "[{ key, header, cell(row, meta), align, sortable, sortKey, card, cardLabel, headerHidden, cardHidden, tableHidden, emptyValue, className }] · a bare string is shorthand for { key, header }", default: "[]" },
      { prop: "rows", values: "the rows, already in display order", default: "[]" },
      { prop: "rowKey", values: "(row, i) => key · a field name · falls back to row.id then the index", default: "row.id" },
      { prop: "card", values: "per column — title · subtitle · aside · meta · actions · hidden", default: "meta" },
      { prop: "sort / onSortChange", values: "{ field, direction } and (field, direction) => void — controlled; no onSortChange means no header sorts", default: "—" },
      { prop: "onRowClick", values: "(row, i, event) => void — row activation; on a card it becomes a pinned chevron control", default: "—" },
      { prop: "rowHref / rowTo + linkComponent", values: "gives the card headline the row's navigation target", default: "—" },
      { prop: "state / stateProps", values: "loading · error · empty · no-results, forwarded to StatePanel", default: "—" },
      { prop: "caption / captionVisible", values: "what the table IS; visually hidden unless captionVisible", default: "— / false" },
      { prop: "density", values: "compact (36px) · comfortable (52px)", default: "compact" },
      { prop: "stickyHeader / footer / forceTable", values: "sticky column header · node below the rows in both presentations · keep the table below 900px", default: "false / — / false" },
      { prop: "rowProps / rowActionLabel / className / ref", values: "(row, i) => extra props for the tr and the card alike · name of the card's open control · passthrough", default: "— / \"Open\"" },
    ],
    guidance: {
      do: [
        "Give every column a card role: title and subtitle carry the row's identity, aside takes the status, actions takes the buttons. Everything left becomes a labelled meta pair.",
        "Keep sorting controlled and let the server do it — hand DataTable rows that are already in the right order, exactly as the live tables already do.",
        "Put the whole cell — the Link, the Button, the Toggle, its disabled and loading state — in `cell`. It is placed verbatim in both presentations, so one definition covers phone and laptop.",
        "Always pass `caption`. It names the table for a screen reader and becomes the card list's label below 900px.",
      ],
      dont: [
        "Don't reach for `forceTable` to avoid designing the phone case — it is for a genuine matrix of figures, and a forced table is the one thing that can put a scrollbar on a phone.",
        "Don't sort or filter inside `cell`; rows arrive in display order and a cell that reorders them will disagree with the header's aria-sort.",
        "Don't invent a new empty or error message here — pass `state` and let StatePanel keep one vocabulary across the app.",
        "Don't hide a column from the card just to tidy it up; `card: \"hidden\"` is for data that genuinely does not belong on a phone.",
      ],
    },
  },

  {
    name: "FiltersBar",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: ["/users/:type?"],
    blurb:
      "The one filter row for every list surface: an elastic search slot, the filter controls, and a trailing slot for a switch or an action. Layout and grouping only — it renders no control, holds no filter state and knows no filter vocabulary, so a page keeps owning its own request shape. One markup at every width: a single row from 900px up, three stacked full-width slots below it, wrapping rather than scrolling so a phone never sees a scrollbar.",
    demo: <FiltersBarDemo />,
    snippet: `<FiltersBar
  label="Filter clients"                   // names the whole row for a screen reader
  search={<SearchInput value={q} onChange={onQ} onClear={clearQ} placeholder="Search by name, email or ID" />}
  actions={<Toggle label="Suppliers" checked={suppliers} onChange={setSuppliers} />}
>
  <Select label="Status" options={STATUS} value={status} onChange={setStatus} />
  <SortSelect options={SORTS} field={sort.field} direction={sort.direction}
    onChange={(field, direction) => setSort({ field, direction })} />
</FiltersBar>`,
    props: [
      { prop: "search", values: "leading elastic slot — normally a <SearchInput>", default: "—" },
      { prop: "children", values: "the filter controls, in the order they should be read (and tabbed)", default: "—" },
      { prop: "actions", values: "trailing slot — a switch, a view toggle, an export button; pushed to the end from 900px up", default: "—" },
      { prop: "label", values: "accessible name for the row; with a search slot the bar is a search landmark, otherwise a labelled group", default: "—" },
      { prop: "className / ref", values: "passthrough — the page adds its own spacing here, never its own layout", default: "—" },
    ],
    guidance: {
      do: [
        "Always pass `label`. It is the only thing that turns a loose run of comboboxes into one named region for a screen-reader user.",
        "Put the controls in `children` in reading order — that is also the tab order, and the bar never reorders them.",
        "Keep the value and the request shape on the page. The bar is where filters are placed, not where they are decided.",
        "Use the trailing `actions` slot for the one control that is not a filter — a scope switch, a view toggle, an export.",
      ],
      dont: [
        "Don't give the bar its own filter state or its own option vocabulary; the moment two pages disagree about a value, a shared bar becomes the wrong place for it.",
        "Don't add a second row of your own below it — pass more controls as children and let them wrap.",
        "Don't restyle the controls through the bar; a Select inside a filter row is the same Select as everywhere else.",
        "Don't put the primary page action here — that belongs to PageHeader.",
      ],
    },
  },

  {
    name: "SortSelect",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: ["/users/:type?"],
    blurb:
      "Select with one job added: it owns the field/direction encoding, so no page keeps a \"created_at|desc\" string in state and splits it on the way into a request. Controlled like DataTable's sort — it stores nothing and reports `onChange(field, direction)`, deliberately the same signature as `onSortChange`, so one handler can drive a sort control and a sortable table header. Same popover on laptop, same bottom sheet on phone; no second look of its own.",
    demo: <SortSelectDemo />,
    snippet: `const SORTS = [
  { field: "created_at", direction: "desc", label: "Last Created" },
  { field: "turnover", direction: "asc", label: "Turnover: Low To High" },
  { field: "turnover", direction: "desc", label: "Turnover: High To Low" },
];

<SortSelect options={SORTS} field={sort.field} direction={sort.direction}
  onChange={(field, direction) => setSort({ field, direction })} />`,
    props: [
      { prop: "options", values: "[{ field, direction, label }] — the API's own sort keys, in menu order", default: "[]" },
      { prop: "field / direction", values: "the pair currently sorted by; controlled, nothing is stored here", default: "—" },
      { prop: "onChange", values: "(field, direction) => void — the same signature as DataTable's onSortChange", default: "—" },
      { prop: "label", values: "accessible name, and the placeholder before anything is sorted", default: "\"Sort\"" },
      { prop: "size / …rest", values: "md · lg, then everything else forwarded to Select", default: "md" },
    ],
    guidance: {
      do: [
        "List the API's real sort keys — the option's `field` and `direction` go into the request untouched.",
        "Drive a sort control and a sortable table header from one handler; both speak (field, direction).",
        "Keep the sorted pair in page state next to the rest of the request, so a refetch has everything it needs.",
      ],
      dont: [
        "Don't sort rows in the browser to match it; like DataTable, this control reports a choice and nothing else.",
        "Don't encode the pair yourself — `field|direction` is this component's private detail and never leaves it.",
        "Don't use it for a filter. A filter narrows the rows; this only reorders them.",
      ],
    },
  },

  {
    name: "ScopeSelect",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: ["/rc/listings", "/alerts", "/leads/:orderLeadType"],
    blurb:
      "One filter-bar dropdown that names whatever is selected. Checkbox rows in a popover, so a set of flags costs one field on the row instead of one switch each, and the current scope stays readable without opening anything. `multiple` (default) = independent flags, none on means everything; single mode keeps the same rows for a payload that only takes one value (an API enum like `region`), and re-picking the ticked row clears it.",
    demo: <ScopeSelectDemo />,
    snippet: `const SCOPES = [
  { key: "consignment", label: "Consignment" },
  { key: "inStock", label: "In stock" },
  { key: "sold", label: "Sold" },
];

<ScopeSelect placeholder="All listings" options={SCOPES}
  value={selected} onChange={setSelected} />`,
    props: [
      { prop: "options", values: "[{ key, label }] — longest label first reads best in the panel", default: "[]" },
      { prop: "value", values: "selected keys (multiple) or one key / null (single); controlled", default: "—" },
      { prop: "onChange", values: "(next) => void — next array (multiple) or key|null (single)", default: "—" },
      { prop: "multiple", values: "false when the payload behind the options takes one value", default: "true" },
      { prop: "placeholder", values: "trigger label while nothing is selected (\"All listings\")", default: "—" },
      { prop: "label / size", values: "accessible name of the panel · trigger size (md · lg)", default: "placeholder · lg" },
    ],
    guidance: {
      do: [
        "Let the trigger say the state: the selected labels, comma-separated, or the \"All …\" placeholder.",
        "Keep the flags and the request shape on the page — this control reports a choice and nothing else.",
        "Use single mode the moment the API takes one value, so the UI cannot express a request the backend can't serve.",
      ],
      dont: [
        "Don't pair it with a tab row over the same dimension; one control per dimension.",
        "Don't add an \"All\" option row — no selection already means everything.",
      ],
    },
  },

  {
    name: "PageFilterBar",
    tier: null,
    status: "shared",
    importFrom: "components/ui",
    sites: ["/alerts", "/rc/listings", "/leads/:orderLeadType", "/users/:type?", "/stock/core-list"],
    blurb:
      "The filter row of a page whose content is a grid: a FIXED-length search pill on the left, every filter control pinned right. The pill length is the page's call — set `--rc-filterbar-search-w`, normally two of that page's cards plus the gap, so the field lines up with the first two grid columns. Renders no control and knows no filter vocabulary. Use FiltersBar instead when the search field should absorb the free width.",
    demo: <PageFilterBarDemo />,
    snippet: `// on the page root: --rc-filterbar-search-w: calc(2 * var(--ls-card-w) + var(--ls-card-gap));

<PageFilterBar label="Filter listings" search={<SearchInput size="lg" … />}>
  <ScopeSelect placeholder="All listings" options={SCOPES} value={scope} onChange={setScope} />
  <Button variant="ghost" size="lg">Filter</Button>
</PageFilterBar>`,
    props: [
      { prop: "search", values: "leading slot — normally a <SearchInput size=\"lg\">", default: "—" },
      { prop: "children", values: "the filter controls, in reading (and tab) order", default: "—" },
      { prop: "label", values: "accessible name for the row; it is a search landmark", default: "—" },
      { prop: "className", values: "passthrough — the page adds its own spacing here, never its own layout", default: "—" },
    ],
    guidance: {
      do: [
        "Set `--rc-filterbar-search-w` on the page root, in that page's own card units, so the pill and the grid step together.",
        "Give every control on the row `size=\"lg\"` — the row sets no heights.",
        "Keep the values and the request shape on the page; the row only places controls.",
      ],
      dont: [
        "Don't size the search field itself — the slot carries the length, and a class on the field loses to `.ft-search`.",
        "Don't use it where the field should stretch; that is FiltersBar's row.",
      ],
    },
  },

];

// PageFilterBar specimen — live, the same composition Manage Listings ships: a fixed
// pill and one scope dropdown. The pill falls back to its default length here; a real
// page sets `--rc-filterbar-search-w` in its own card units.
function PageFilterBarDemo() {
  const [q, setQ] = React.useState("");
  const [scope, setScope] = React.useState([]);
  return (
    <PageFilterBar
      label="Filter listings"
      search={(
        <SearchInput
          size="lg"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onClear={() => setQ("")}
          placeholder="Quick search for products ..."
          aria-label="Search listings"
        />
      )}
    >
      <ScopeSelect
        placeholder="All listings"
        options={[
          { key: "consignment", label: "Consignment" },
          { key: "inStock", label: "In stock" },
          { key: "sold", label: "Sold" },
        ]}
        value={scope}
        onChange={setScope}
      />
    </PageFilterBar>
  );
}

// ScopeSelect specimen — the Manage Listings scope control, live: three independent
// flags behind one trigger that names whatever is on.
function ScopeSelectDemo() {
  const [selected, setSelected] = React.useState(["inStock"]);
  return (
    <ScopeSelect
      label="Listing scope"
      placeholder="All listings"
      options={[
        { key: "consignment", label: "Consignment" },
        { key: "inStock", label: "In stock" },
        { key: "sold", label: "Sold" },
      ]}
      value={selected}
      onChange={setSelected}
    />
  );
}

// FiltersBar specimen — live, and deliberately the same composition the Users toolbar ships:
// search, a status Select, a SortSelect, and a trailing switch. Resize the guide and the row
// stacks at 900px, which is the only way to SEE what the primitive does.
function FiltersBarDemo() {
  const [q, setQ] = React.useState("");
  const [status, setStatus] = React.useState("show_all");
  const [sort, setSort] = React.useState({ field: "created_at", direction: "desc" });
  const [suppliers, setSuppliers] = React.useState(false);

  return (
    <FiltersBar
      label="Filter clients"
      search={
        <SearchInput
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onClear={() => setQ("")}
          placeholder="Search by name, email or ID"
          aria-label="Search clients"
        />
      }
      actions={<Toggle label="Suppliers" labelPosition="start" checked={suppliers} onChange={setSuppliers} />}
    >
      <Select
        label="Status"
        options={[
          { value: "show_all", label: "Show All" },
          { value: "claimed", label: "Claimed" },
          { value: "unclaimed", label: "Unclaimed" },
        ]}
        value={status}
        onChange={setStatus}
      />
      <SortSelect
        options={SORT_SELECT_OPTIONS}
        field={sort.field}
        direction={sort.direction}
        onChange={(field, direction) => setSort({ field, direction })}
      />
    </FiltersBar>
  );
}

const SORT_SELECT_OPTIONS = [
  { field: "created_at", direction: "desc", label: "Last Created" },
  { field: "turnover", direction: "asc", label: "Turnover: Low To High" },
  { field: "turnover", direction: "desc", label: "Turnover: High To Low" },
  { field: "last_purchase", direction: "desc", label: "Last purchase: New first" },
];

function SortSelectDemo() {
  const [sort, setSort] = React.useState({ field: "created_at", direction: "desc" });
  return (
    <SortSelect
      options={SORT_SELECT_OPTIONS}
      field={sort.field}
      direction={sort.direction}
      onChange={(field, direction) => setSort({ field, direction })}
    />
  );
}

// DataTable specimen — a live one on purpose: the sort header actually re-sorts (in local state,
// so the demo needs no backend), the toggle and the Remove button actually fire, and the same
// definitions produce the table above 900px and the card list below it. Resize the guide and the
// specimen switches, which is the only way to SEE the milestone.
const DATA_TABLE_ROWS = [
  { id: "cl1", reference: "115234-0002", brand: "Rolex", model: "Datejust 36", status: "active", demand: 4, price: 9000, active: true },
  { id: "cl2", reference: "310.30.42", brand: "Omega", model: "Speedmaster Professional", status: "reserved", demand: 2, price: 4200, active: false },
  { id: "cl3", reference: "5711/1A-010", brand: "Patek Philippe", model: "Nautilus", status: "sold", demand: 9, price: 118000, active: false },
];

function DataTableDemo() {
  const [sort, setSort] = React.useState({ field: "reference", direction: "asc" });
  const [rows, setRows] = React.useState(DATA_TABLE_ROWS);

  // The guide sorts locally because it has no server; a live table hands `rows` back already
  // sorted by the API and only reports the change. Both use the same controlled `sort` prop.
  const sorted = React.useMemo(() => {
    const dir = sort.direction === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => String(a[sort.field]).localeCompare(String(b[sort.field]), "en") * dir);
  }, [rows, sort]);

  const columns = React.useMemo(
    () => [
      { key: "reference", header: "Reference", sortable: true, card: "title" },
      { key: "model", header: "Model", card: "subtitle" },
      { key: "status", header: "Stock state", card: "aside", cell: (row) => <Badge status={row.status} dot /> },
      { key: "demand", header: "Demand 30d", align: "end", sortable: true },
      { key: "price", header: "Price", align: "end", cell: (row) => <Money value={row.price} major currency="GBP" decimals={0} /> },
      {
        key: "listing",
        header: "Listing",
        cell: (row) => (
          <Toggle
            tone="active"
            checked={row.active}
            disabled={row.status === "sold"}
            label={row.active ? "Active" : "Off"}
            onChange={(active) => setRows((all) => all.map((r) => (r.id === row.id ? { ...r, active } : r)))}
          />
        ),
      },
      {
        key: "actions",
        header: "Actions",
        headerHidden: true,
        card: "actions",
        cell: (row) => (
          <Button size="md" variant="outline" onClick={() => setRows((all) => all.filter((r) => r.id !== row.id))}>
            Remove
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <div className="sg-stack">
      <DataTable
        caption="Core List references"
        columns={columns}
        rows={sorted}
        sort={sort}
        onSortChange={(field, direction) => setSort({ field, direction })}
        footer={<span>{sorted.length} of {DATA_TABLE_ROWS.length} references</span>}
      />
      {/* The empty state is the same StatePanel vocabulary, inside the table's own body. */}
      <DataTable
        caption="Core List references — empty"
        columns={columns}
        rows={[]}
        state="empty"
        stateProps={{ title: "Core List is empty", description: "Add the first reference and it'll show up here.", action: { label: "Add products" } }}
      />
    </div>
  );
}

// Small stateful demo so the Toggle in the registry is interactive.
function ToggleDemo() {
  const [on, setOn] = React.useState(true);
  return (
    <>
      <Toggle label="Default" checked={on} onChange={setOn} />
      <Toggle label="Active" tone="active" checked={on} onChange={setOn} />
      <Toggle label="Reserved" tone="reserved" checked={on} onChange={setOn} />
    </>
  );
}

export { HERO_MODIFIER };
